import { Platform } from 'react-native';

const INVALID_CREDENTIAL_CODES = new Set([
  'auth/wrong-password',
  'auth/user-not-found',
  'auth/invalid-credential',
  'auth/invalid-login-credentials',
  'auth/user-disabled',
]);

type FirebaseAuthError = {
  code?: string;
  message?: string;
};

export function getAuthErrorMessage(
  error: unknown,
  fallback = 'Failed to log in. Please try again.'
): string {
  const err = error as FirebaseAuthError;
  const code = err?.code ?? '';
  const message = err?.message ?? '';

  if (message.includes('Duplicate credential') || code === 'auth/unknown' && message.includes('Duplicate')) {
    return 'Apple sign-in token was already used. Tap Sign in with Apple again.';
  }

  if (code === 'auth/invalid-credential' || code === 'auth/missing-or-invalid-nonce' || code === 'auth/internal-error') {
    if (message.includes('error=invalid_client') && message.includes('appleid.apple.com')) {
      return (
        'Apple rejected Firebase’s server credentials (invalid_client). In Firebase → Authentication → Apple, ' +
        're-check Team ID, Key ID (must match your .p8 filename), and paste the full Sign in with Apple .p8 key—not an App Store Connect API key.'
      );
    }
    if (code === 'auth/missing-or-invalid-nonce' || message.includes('MissingOrInvalidNonce')) {
      return (
        'Apple sign-in security check failed (nonce mismatch). Reload the app and try again. ' +
        'If it persists, run npx expo run:ios to reinstall the dev build.'
      );
    }
    if (Platform.OS === 'ios' || Platform.OS === 'android') {
      if (message.includes('wrong app identity') || message.includes('Expected "com.expensewatcher.goalmint"')) {
        return message;
      }
      return (
        'Apple sign-in could not be verified by Firebase. In Firebase Console → Authentication → Sign-in method, ' +
        'enable Apple. On iOS, also confirm the app bundle ID is com.expensewatcher.goalmint and rebuild with npx expo run:ios.'
      );
    }
    return (
      'Apple sign-in could not be completed. In Firebase → Authentication → Apple, verify Team ID, ' +
      'Key ID, and the full .p8 private key (including BEGIN/END lines), then save and wait a few minutes.'
    );
  }
  if (INVALID_CREDENTIAL_CODES.has(code)) {
    return 'Invalid email or password';
  }
  if (code === 'auth/too-many-requests') {
    return 'Too many failed attempts. Please try again later.';
  }
  if (code === 'auth/network-request-failed') {
    return 'Network error. Please check your connection and try again.';
  }
  if (code === 'auth/invalid-email') {
    return 'Please enter a valid email address';
  }
  if (code === 'auth/account-exists-with-different-credential') {
    return 'An account already exists with this email using a different sign-in method.';
  }
  if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
    return '';
  }
  if (code === 'auth/operation-not-allowed') {
    return 'This sign-in method is not enabled. Check Firebase Authentication settings.';
  }
  if (code === 'auth/unauthorized-domain') {
    return 'This domain is not authorized for sign-in. Add it under Firebase Authentication → Settings → Authorized domains.';
  }
  if (code === 'auth/popup-blocked') {
    return 'Sign-in popup was blocked. Allow popups for this site or try again.';
  }

  if (
    INVALID_CREDENTIAL_CODES.has(message) ||
    message.includes('auth/wrong-password') ||
    message.includes('auth/user-not-found') ||
    message.includes('auth/invalid-credential') ||
    message.includes('auth/invalid-login-credentials')
  ) {
    return 'Invalid email or password';
  }
  if (message.includes('auth/too-many-requests')) {
    return 'Too many failed attempts. Please try again later.';
  }
  if (message.includes('auth/network-request-failed')) {
    return 'Network error. Please check your connection and try again.';
  }

  if (message.includes('not configured')) {
    return message;
  }

  return fallback;
}

export function isInvalidCredentialError(error: unknown): boolean {
  return getAuthErrorMessage(error) === 'Invalid email or password';
}

/** Include Firebase error code in UI for Apple/Google debugging. */
export function formatSocialAuthError(error: unknown, fallback: string): string {
  const err = error as FirebaseAuthError;
  const message = getAuthErrorMessage(error, fallback);
  const code = err?.code ?? '';
  if (!message) {
    return '';
  }
  return code ? `${message} (${code})` : message;
}

export function isSocialSignInCancelled(error: unknown): boolean {
  const err = error as { code?: string; message?: string };
  if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
    return true;
  }
  if (err?.code === 'ERR_REQUEST_CANCELED') {
    return true;
  }
  const message = err?.message ?? '';
  return message.toLowerCase().includes('cancel');
}
